import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TrustpilotRating {
  rating: number | null;
  review_count: number | null;
  scraped_at: string;
  trustpilot_url?: string;
}

// Static fallback values for when DB data is not available
const FALLBACK_RATINGS: Record<string, { rating: number; reviewCount: number }> = {
  // Lån utan UC
  'lan-utan-uc:loanstep': { rating: 4.7, reviewCount: 3502 },
  'lan-utan-uc:fairlo': { rating: 4.6, reviewCount: 4333 },
  'lan-utan-uc:ferratum': { rating: 4.4, reviewCount: 1130 },
  'lan-utan-uc:brixo': { rating: 4.6, reviewCount: 2969 },
  'lan-utan-uc:flexkontot': { rating: 4.7, reviewCount: 1795 },
  'lan-utan-uc:merax': { rating: 4.7, reviewCount: 1298 },
  // Företagslån
  'foretagslan:krea': { rating: 4.8, reviewCount: 491 },
  'foretagslan:froda': { rating: 4.9, reviewCount: 1668 },
  'foretagslan:qred': { rating: 4.7, reviewCount: 1092 },
  'foretagslan:fedelta': { rating: 4.8, reviewCount: 135 },
  'foretagslan:opr-foretagslan': { rating: 4.6, reviewCount: 351 },
  'foretagslan:capitalbox': { rating: 4.5, reviewCount: 200 },
  // Providers (privatlån)
  'provider:sambla': { rating: 4.8, reviewCount: 29456 },
  'provider:lanekoll2': { rating: 4.7, reviewCount: 2102 },
  'provider:trygga': { rating: 4.6, reviewCount: 1253 },
  'provider:lendo': { rating: 4.5, reviewCount: 3373 },
  'provider:enklare': { rating: 4.5, reviewCount: 1500 },
  'provider:advisa': { rating: 4.6, reviewCount: 2500 },
  'provider:zensum': { rating: 4.5, reviewCount: 1200 },
  'provider:thorn': { rating: 4.4, reviewCount: 800 },
  'provider:plus1': { rating: 4.3, reviewCount: 500 },
  'provider:anyfin': { rating: 4.6, reviewCount: 3000 },
};

/**
 * Hook to fetch Trustpilot rating for a single entity with fallback
 */
export function useTrustpilotRating(entityType: string, entitySlug: string) {
  const fallbackKey = `${entityType}:${entitySlug}`;
  const fallback = FALLBACK_RATINGS[fallbackKey];

  return useQuery({
    queryKey: ['trustpilot-rating', entityType, entitySlug],
    queryFn: async (): Promise<TrustpilotRating | null> => {
      const { data, error } = await supabase
        .from('trustpilot_ratings')
        .select('rating, review_count, scraped_at, trustpilot_url')
        .eq('entity_type', entityType)
        .eq('entity_slug', entitySlug)
        .maybeSingle();

      if (error) {
        console.error('Error fetching Trustpilot rating:', error);
        return null;
      }

      return data as TrustpilotRating | null;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - ratings don't change often
    select: (data) => {
      // Return DB data if available, otherwise use fallback
      if (data?.rating !== null && data?.review_count !== null) {
        return {
          rating: data.rating,
          reviewCount: data.review_count,
          trustpilotUrl: data.trustpilot_url,
          isFromDb: true,
        };
      }
      // Use fallback if DB data not available
      if (fallback) {
        return {
          rating: fallback.rating,
          reviewCount: fallback.reviewCount,
          trustpilotUrl: undefined,
          isFromDb: false,
        };
      }
      return null;
    },
  });
}

/**
 * Hook to fetch all Trustpilot ratings
 */
export function useAllTrustpilotRatings() {
  return useQuery({
    queryKey: ['trustpilot-ratings-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trustpilot_ratings')
        .select('*')
        .order('entity_name', { ascending: true });

      if (error) {
        console.error('Error fetching Trustpilot ratings:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch Trustpilot ratings for banks only
 */
export function useBankTrustpilotRatings() {
  return useQuery({
    queryKey: ['trustpilot-ratings-banks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trustpilot_ratings')
        .select('*')
        .eq('entity_type', 'bank')
        .order('entity_name', { ascending: true });

      if (error) {
        console.error('Error fetching bank Trustpilot ratings:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Hook to fetch Trustpilot ratings for providers only
 */
export function useProviderTrustpilotRatings() {
  return useQuery({
    queryKey: ['trustpilot-ratings-providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trustpilot_ratings')
        .select('*')
        .eq('entity_type', 'provider')
        .order('entity_name', { ascending: true });

      if (error) {
        console.error('Error fetching provider Trustpilot ratings:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}
